import { useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, CheckIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useFlags } from 'launchdarkly-react-client-sdk';
import BasicModal from './BasicModal';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { canAccessReferralPage } from '../../utils/referralProgramConfig';
import InviteOthersForm from '../InviteOthersForm';
import ReferralBonusFootnote from './ReferralBonusFootnote';
import ReferralRewardDetail from './ReferralRewardDetail';
import { WithTooltip } from '../WithTooltip';
import {
  enrollInReferralProgram,
  getMyReferralStatus,
  sendReferralInvites,
} from '../../services/ReferralService';
import { useReferralCode } from '../../hooks/useShareUrl';
import { isValidEmail } from '../../utils/email';
import {
  buildInvitePayload,
  hasPayableBonusTier,
  inviteSendTrackingProps,
  isClaimableReward,
  mergeInviteOutcomes,
  splitAddressInput,
  unsentAddresses,
} from '../../utils/inviteOthers';

// Not "invites": this is a list of teachers to share with, not people being invited to join
// something.
const GENERIC_SEND_ERROR = 'Something went wrong sending those. Please try again.';
const SEND_HINT_EMPTY = 'Enter an email address first';
const SEND_HINT_ALL_SENT = 'Those are already on their way';
const SEND_HINT_MS = 2500;
// For a program with no modal_subhead set, or a lookup that failed.
const FALLBACK_PROGRAM_SUBHEAD = 'Earn rewards for every teacher you bring to Diffit.';

/**
 * ShareReferralModal — an invite ask on the referral infra, reusing the onboarding wizard's
 * invite field. Two callers: the math promo's free-year gift (MathPromoBanner) and the generic
 * "invite a colleague" in-resource ad (the referral descriptor in the ad registry).
 *
 * One component rather than two siblings: the two asks differ only in a subhead, an aria label,
 * an enrolment surface, two program keys and an optional banner. Everything else is hardcoded.
 *
 * Two tiers at the top, not three: a text headline and a text subhead. The premium promise is
 * the subhead itself — no card, no crown — and the swag bonus is a footnote at the bottom.
 * A card plus a bonus line above the field made the ask compete with itself.
 *
 * `banner` adds a caller-owned band above the ask and gates nothing else: everything below it is
 * identical either way.
 *
 * Opening it POSTs /referral/enroll rather than reading /referral/me when the caller names an
 * `enrolmentSurface` — see enrollInReferralProgram for why, and why that is a surface and not a
 * key. Callers whose teachers are already enrolled leave it off and get the status read alone.
 *
 * A live campaign is the caller's precondition, not this modal's: both callers settle the
 * program key before they can open it. So there is no no-campaign design here.
 *
 * That leaves one gate: `hasBonusProgram`, over only the swag footnote, which promises the
 * *sharer* a payout. `programs` is per-teacher — async_get_assigned_programs joins
 * referral_assignment on user_id — so it is empty for a teacher the enrolment above couldn't
 * place, and `next_tier: null` means they have earned the whole ladder. The subhead is about the
 * people they invite, so it always shows.
 *
 * `inviteProgramKey` and `bonusProgramKey` stay separate: the math caller sends invites against
 * bts_2026_math but gates the footnote on bts_2026_september_scramble, whose swag terms it
 * states. One key for both would hand hasPayableBonusTier the wrong campaign, which answers
 * "no payable tier" exactly as it does for a teacher in no program — the footnote just stops.
 *
 * `bonusProgramKey` is optional, and omitting it suppresses the footnote outright rather than
 * asking about a tier and getting no for a different reason. The in-resource referral ad leaves
 * it off: it states the terms in its own subhead and keeps the "See your rewards" button, which
 * goes to the dashboard's live progress rather than to a promise.
 */
const ShareReferralModal = ({
  isOpen,
  onClose,
  ariaLabel,
  // Optional: names the surface to enrol through. Omitted for a caller whose teachers the
  // backend has already enrolled, which then costs no enrolment request at all.
  enrolmentSurface = null,
  // Names the campaign whose invite email goes out, and the one invites are credited to.
  inviteProgramKey,
  // Which surface opened this modal, from src/constants/linkSources.js. Rides into the invite
  // email's `ref_src`. Caller-owned like `trackingPrefix`, and for the same reason: every
  // surface opens the identical modal, so nothing here can tell them apart.
  inviteSource,
  // Names the campaign whose swag terms ReferralBonusFootnote states. See the note above.
  bonusProgramKey,
  headline = 'Share the love',
  // Omitted to show the invite program's `copy.modal_subhead`, so the terms follow its config.
  subhead,
  banner = null,
  placeholder,
  // Whether to offer the copy-link and "See your rewards" links under the field. The
  // workshop's share-for-premium ask turns them off: its design is the ask alone, and the
  // teacher opening it hasn't been to the dashboard to have rewards to see yet.
  showSecondaryActions = true,
  onTrack,
  trackingPrefix,
}) => {
  // `inviteDraft` is text typed but not yet committed to a chip — Send picks it up too.
  const [selections, setSelections] = useState({ invited: [], inviteDraft: '' });
  const [hasBonusProgram, setHasBonusProgram] = useState(false);
  const [programSubhead, setProgramSubhead] = useState(null);
  const [bonusFootnote, setBonusFootnote] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Cumulative across submits: a response only covers the batch it was sent, so the last one
  // alone can't label chips from an earlier one.
  const [outcomes, setOutcomes] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [reward, setReward] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Send stays green with nothing to send rather than greying out — a live CTA reads as the
  // next step. Pressing it anyway explains itself here instead of silently doing nothing.
  const [sendHint, setSendHint] = useState(null);
  const sendHintTimer = useRef(null);
  const referralCode = useReferralCode();

  const { diffitUser } = useDiffitUserContext();
  const { referralProgramConfiguration } = useFlags();
  const canSeeDashboard = canAccessReferralPage(diffitUser, referralProgramConfiguration);

  // Built here rather than taken from the response's `share_url`: that one is built from the
  // backend's BASE_URL, so it points at the wrong origin outside prod — and it is absent
  // altogether when the referral flag is off, which is exactly the state this half still has to
  // hand out a link in. /r/<code> stores attribution for the eventual POST /user.
  const shareLink = referralCode && `${window.location.origin}/r/${referralCode}`;

  // On open, not on mount: one request per teacher who reaches for the flow, not per packet.
  useEffect(() => {
    if (!isOpen) return;
    // A reopen is a fresh ask. `outcomes` and the chips are deliberately cumulative, but a
    // reward card re-shown on open reads as a second payout, and a stale error or a mid-flight
    // "Copied" belongs to the visit that produced it.
    setReward(null);
    setSubmitError(null);
    setSendHint(null);
    setLinkCopied(false);
    let cancelled = false;
    // With a surface: enrol, but fall back to a plain status read if that endpoint isn't there.
    // A 404 is ambiguous — referral flag off, or POST /referral/enroll not deployed yet — and a
    // frontend released ahead of the backend would otherwise hide the whole email half. GET
    // /referral/me has always existed, so if it answers, the ask still works for anyone already
    // enrolled.
    //
    // Without one there is nothing to enrol, so the status read is the whole lookup.
    const lookup = enrolmentSurface
      ? enrollInReferralProgram(enrolmentSurface).catch(() => getMyReferralStatus())
      : getMyReferralStatus();
    lookup
      .then((response) => {
        if (cancelled) return;
        const inviteProgram = response?.data?.programs?.find((p) => p.key === inviteProgramKey);
        setProgramSubhead(inviteProgram?.copy?.modal_subhead || FALLBACK_PROGRAM_SUBHEAD);
        setBonusFootnote(
          response?.data?.programs?.find((p) => p.key === bonusProgramKey)?.copy?.bonus_footnote
        );
        // No bonusProgramKey means the caller does not want the swag footnote at all, so the
        // tier lookup is skipped rather than answered — see the prop's note above.
        setHasBonusProgram(
          !!bonusProgramKey && hasPayableBonusTier(response?.data?.programs, bonusProgramKey)
        );
      })
      .catch(() => {
        if (cancelled) return;
        setProgramSubhead(FALLBACK_PROGRAM_SUBHEAD);
        // Only the footnote is withheld. The ask itself stays: the caller already established
        // that the campaign is live, and a failed lookup is no reason to retract the offer.
        setHasBonusProgram(false);
      });
    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the open transition alone: the surface and the bonus key are constant
    // per call site, so listing them would only risk re-firing the lookup.
  }, [isOpen]);

  useEffect(() => () => clearTimeout(sendHintTimer.current), []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
    onTrack?.(`${trackingPrefix} Link Copied`);
  };

  const handleSend = async () => {
    setSubmitError(null);
    // Split and validate the draft exactly as InviteOthersForm's commit() does — the draft is
    // where commit() leaves what it rejected, so sending it raw would post those tokens.
    const emails = buildInvitePayload([
      ...selections.invited,
      ...splitAddressInput(selections.inviteDraft).filter(isValidEmail),
    ]);
    if (emails.length === 0) return;
    // Every address needs a chip for its outcome label to attach to.
    setSelections((prev) => ({ ...prev, invited: emails, inviteDraft: '' }));
    // Re-sending an answered address comes back skipped/already_invited and burns a slot in
    // the per-request cap.
    const unsent = unsentAddresses(emails, outcomes);
    if (unsent.length === 0) return;

    setIsSubmitting(true);
    try {
      const response = await sendReferralInvites(unsent, inviteProgramKey, inviteSource);
      setOutcomes((prev) => mergeInviteOutcomes(prev, response.data));
      const earned = (response.data.rewards || [])[0];
      if (earned && isClaimableReward(earned)) setReward(earned);
      onTrack?.(`${trackingPrefix} Invites Sent`, {
        ...inviteSendTrackingProps(unsent, response.data),
        reward_earned: !!earned,
      });
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message || GENERIC_SEND_ERROR);
      onTrack?.(`${trackingPrefix} Send Failed`, { error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Same two inputs handleSend reads, so the hint can't disagree with what Send would do.
  const hasSomethingToSend =
    unsentAddresses(selections.invited, outcomes).length > 0 ||
    splitAddressInput(selections.inviteDraft).filter(isValidEmail).length > 0;

  // Every address already has an outcome — a second press after a successful send. Saying
  // "enter an email address" there would be wrong: they entered them and we sent them.
  const hasAddresses =
    selections.invited.length > 0 ||
    splitAddressInput(selections.inviteDraft).filter(isValidEmail).length > 0;

  const handleSendClick = () => {
    clearTimeout(sendHintTimer.current);
    if (!hasSomethingToSend) {
      setSendHint(hasAddresses ? SEND_HINT_ALL_SENT : SEND_HINT_EMPTY);
      sendHintTimer.current = setTimeout(() => setSendHint(null), SEND_HINT_MS);
      return;
    }
    setSendHint(null);
    handleSend();
  };

  const sendButton = (
    <WithTooltip
      text={sendHint}
      position='top'
      anchor='right'
      showArrow
      forceShow={!!sendHint}
      // Nothing to show on hover anyway (`text` *is* the hint) — this just states that intent.
      disabled={!sendHint}
      // Mirrors the content's pb-8, so the CTA band keeps equal air above and below.
      className='mt-8 w-full'>
      <button
        type='button'
        onClick={handleSendClick}
        disabled={isSubmitting}
        // border-transparent, not no border: Copy link below carries a 1px one, and without a
        // match here the two stacked CTAs come out 36px and 38px.
        className='w-full rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'>
        <span>{isSubmitting ? 'Sending...' : 'Send'}</span>
      </button>
    </WithTooltip>
  );

  return (
    // Own padding and heading rather than BasicModal's centered `title`, matching the
    // bts-2026-freemium modals (AddSkillsModal). No footer: the X is the only exit needed.
    <BasicModal
      showModal={isOpen}
      // BasicModal only ever calls this with false, so a plain close handler is the whole
      // contract — the callers of this modal own their own open state.
      setShowModal={onClose}
      ariaLabel={ariaLabel}
      // A no-op for callers that mount closed; the paygate offer mounts already open.
      appear
      dialogPadding=''
      maxWidth='sm:max-w-xl'
      // 12px, against the 16px every other modal uses, so the band below needs no deep gutter.
      closeButtonInset='pr-3 pt-3'>
      <div className='px-8 pb-8 pt-10 sm:px-12'>
        {banner && (
          <>
            {banner}
            {/* Same mb-6 as the band above, so the rule sits centred between the two. */}
            <hr className='mb-6 border-gray-200' />
          </>
        )}
        {/* Caller-supplied, like the subhead: the two asks are not the same ask. The math
            caller's is about giving colleagues a free year, so its reward to the sharer is
            incidental; the generic ad's is about what the sharer earns. Defaults to the math
            wording so that caller needs no prop. */}
        <h1 className='text-xl font-semibold text-gray-800 sm:text-2xl'>
          <span>{headline}</span>
        </h1>
        <p className='mt-2 text-base text-gray-600'>{subhead ?? programSubhead}</p>

        <div className='mt-6'>
          {/* No `progress`, so no "n of threshold" counter: the response's progress describes the
              *invited*-step program (_async_invited_program_progress), not necessarily the one
              paying this bonus, and a repeating tier has no threshold anyway. */}
          <InviteOthersForm
            selections={selections}
            setSelections={setSelections}
            isSubmitting={isSubmitting}
            outcomes={outcomes}
            submitError={submitError}
            programKey={inviteProgramKey}
            action={sendButton}
            placeholder={placeholder}
            size='compact'
          />
        </div>

        {reward && (
          <div className='mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-4'>
            <ReferralRewardDetail reward={reward} />
          </div>
        )}

        {showSecondaryActions && shareLink && (
          <button
            type='button'
            onClick={handleCopyLink}
            className='mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'>
            {linkCopied ? (
              <>
                <CheckIcon className='h-4 w-4 text-emerald-600' />
                <span>Copied</span>
              </>
            ) : (
              <>
                <LinkIcon className='h-4 w-4' />
                {/* "your unique link" rather than "link", matching the referral dashboard's own
                    SHARE YOUR UNIQUE LINK label — it is the same /r/<code> either way. */}
                <span>Copy your unique link</span>
              </>
            )}
          </button>
        )}

        {showSecondaryActions && canSeeDashboard && !bonusProgramKey && (
          // A new tab, like the in-resource ads' own link-outs: this modal opens over a packet
          // the teacher is working on, and routing away in place would cost them it.
          <a
            href='/referral'
            target='_blank'
            rel='noopener noreferrer'
            className='mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'>
            <span>See your rewards</span>
            <ArrowTopRightOnSquareIcon className='h-4 w-4' />
          </a>
        )}

        {hasBonusProgram && <ReferralBonusFootnote text={bonusFootnote} />}
      </div>
    </BasicModal>
  );
};

export default ShareReferralModal;
